import * as React from "react";
import { Link, type LinkProps } from "react-router-dom";

import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";
import { cn } from "utils/cn";
import { useAppSidebarLayout } from "./appSidebarLayout";

/** Shared focus ring for every interactive element in the sidebar. */
export const sidebarFocusRingClassName =
  "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary";

/** The surface a nav list sits on: the sidebar chrome, or a popover such as a step flyout. */
export type NavSurface = "chrome" | "popover";

const NAV_ACTIVE_LABEL_CLASSES: Record<NavSurface, string> = {
  chrome: "text-chrome-foreground ring-chrome-foreground/20 hover:text-chrome-foreground",
  popover: "text-popover-foreground ring-popover-foreground/20 hover:text-popover-foreground",
};

/**
 * The one active style for every navigation list: sidebar rows, project step items,
 * collapsed icons and step flyouts. Badge language: a brand tint, a hairline inset ring
 * (no layout shift) and medium weight. No left bar. Hover keeps it as is.
 *
 * The label keeps the surface's own foreground. `text-primary` on the tint falls below
 * 4.5:1 in 17 of the 28 preset and mode pairs (every light preset); the foreground
 * measures 4.60:1 or more in all 28, on the sidebar and in flyouts. The ring uses the
 * foreground too, because in presets whose primary matches their chrome (paper, sage,
 * tungsten and noir light) the brand tint alone is invisible.
 */
export const navActiveClassName = (surface: NavSurface = "chrome") =>
  cn(
    "bg-primary/[0.08] font-medium ring-1 ring-inset hover:bg-primary/[0.08]",
    NAV_ACTIVE_LABEL_CLASSES[surface]
  );

export const sidebarRowClassName = (options: { active?: boolean; collapsed?: boolean }) =>
  cn(
    "relative flex min-h-8 w-full min-w-0 items-center gap-2.5 rounded-[7px] border-0 bg-transparent px-2 py-1.5",
    "text-left text-[13px] text-chrome-foreground transition-colors hover:bg-chrome-muted",
    sidebarFocusRingClassName,
    options.collapsed && "justify-center px-0 py-2",
    options.active && navActiveClassName("chrome")
  );

const iconClassName = "h-4 w-4 shrink-0";

interface SidebarRowContentProps {
  icon?: React.ReactNode;
  label: string;
  /** Right side of the row (badges, counts). Hidden when collapsed. */
  trailing?: React.ReactNode;
  /** Extra content kept visible when collapsed, e.g. a status dot on the icon. */
  collapsedAdornment?: React.ReactNode;
  labelClassName?: string;
}

const SidebarRowContent = ({
  icon,
  label,
  trailing,
  collapsedAdornment,
  labelClassName,
}: SidebarRowContentProps) => {
  const { collapsed } = useAppSidebarLayout();
  return (
    <>
      {icon}
      {collapsed ? (
        <>
          <span className="sr-only">{label}</span>
          {collapsedAdornment}
        </>
      ) : (
        <>
          <span className={cn("min-w-0 flex-1 truncate", labelClassName)}>{label}</span>
          {trailing}
        </>
      )}
    </>
  );
};

/** Wraps a row in a right-side tooltip while the sidebar is collapsed. */
export const CollapsedTooltip = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactElement;
}) => {
  const { collapsed } = useAppSidebarLayout();
  if (!collapsed) return children;
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

type SidebarRowButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  SidebarRowContentProps & { active?: boolean };

/** A sidebar row that runs an action, or triggers a menu when used with `asChild` triggers. */
export const SidebarRowButton = React.forwardRef<HTMLButtonElement, SidebarRowButtonProps>(
  function SidebarRowButton(
    {
      icon,
      label,
      trailing,
      collapsedAdornment,
      labelClassName,
      active,
      className,
      type = "button",
      ...buttonProps
    },
    ref
  ) {
    const { collapsed } = useAppSidebarLayout();
    return (
      <button
        ref={ref}
        type={type}
        className={cn(sidebarRowClassName({ active, collapsed }), className)}
        {...buttonProps}
      >
        <SidebarRowContent
          icon={icon}
          label={label}
          trailing={trailing}
          collapsedAdornment={collapsedAdornment}
          labelClassName={labelClassName}
        />
      </button>
    );
  }
);

type SidebarRowLinkProps = Omit<LinkProps, "children"> &
  SidebarRowContentProps & { active?: boolean };

/** A sidebar row that navigates. A plain Link, so cmd and middle click keep working. */
export const SidebarRowLink = React.forwardRef<HTMLAnchorElement, SidebarRowLinkProps>(
  function SidebarRowLink(
    { icon, label, trailing, collapsedAdornment, labelClassName, active, className, ...linkProps },
    ref
  ) {
    const { collapsed } = useAppSidebarLayout();
    return (
      <Link
        ref={ref}
        aria-current={active ? "page" : undefined}
        className={cn(sidebarRowClassName({ active, collapsed }), className)}
        {...linkProps}
      >
        <SidebarRowContent
          icon={icon}
          label={label}
          trailing={trailing}
          collapsedAdornment={collapsedAdornment}
          labelClassName={labelClassName}
        />
      </Link>
    );
  }
);

/** Renders a lucide icon at the sidebar's icon size. */
export const SidebarIcon = ({
  icon: Icon,
}: {
  icon: React.ComponentType<{ className?: string }>;
}) => <Icon className={iconClassName} aria-hidden />;

/** Uppercase group label. Collapsed, it becomes a thin divider. */
export const SidebarSectionLabel = ({ children }: { children: string }) => {
  const { collapsed } = useAppSidebarLayout();
  if (collapsed) {
    return (
      <div className="px-2.5 pb-1 pt-2.5" role="separator" aria-label={children}>
        <div className="h-px bg-chrome-border" />
      </div>
    );
  }
  return (
    <div className="px-2 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-chrome-muted-foreground">
      {children}
    </div>
  );
};
